/**
 * Turn a v2 run's node tree into what the canvas shows: sorted siblings at
 * every level, date-bucket labels formatted per granularity, and cell text
 * for the visible metric columns. Pure, so the table, CSV export and print
 * view all walk the same sorted tree.
 *
 * Default order (no sort chosen): date buckets newest first, every other
 * level A–Z. A chosen sort applies within every level, including the dates.
 */
import { TreeTableNode } from "../../../common/GroupedTreeTable";
import {
  BucketRange,
  formatBucketLabel,
} from "../../../../utils/dateRangeFormat";
import { formatMetricValue } from "../../../../utils/metricFormat";
import {
  ReportGranularity,
  ReportMetricDef,
  ReportTreeNodeV2,
  RunReportV2Response,
  ViewSort,
} from "../../../../types/reportsV2";
import { LABEL_SORT_KEY } from "./viewConfig";

export type MetricIndex = ReadonlyMap<string, number>;

export const metricIndexOf = (response: RunReportV2Response): MetricIndex =>
  new Map(response.metric_keys.map((key, i) => [key, i]));

/** A node's value for a metric key; null when unknown or unavailable. */
export function valueOf(
  values: Array<number | null>,
  index: MetricIndex,
  key: string,
): number | null {
  const i = index.get(key);
  if (i === undefined) return null;
  const v = values[i];
  return typeof v === "number" && !Number.isNaN(v) ? v : null;
}

const compareLabels = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

function compareNodes(
  a: ReportTreeNodeV2,
  b: ReportTreeNodeV2,
  depth: number,
  sort: ViewSort | null,
  index: MetricIndex,
): number {
  const byLabel = (dir: number) =>
    depth === 0
      ? dir * (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
      : dir * compareLabels(a.label, b.label);
  if (!sort) return byLabel(depth === 0 ? -1 : 1);
  const dir = sort.dir === "asc" ? 1 : -1;
  if (sort.key === LABEL_SORT_KEY) return byLabel(dir);
  const va = valueOf(a.values, index, sort.key);
  const vb = valueOf(b.values, index, sort.key);
  // Blank values sort last in either direction.
  if (va === null || vb === null) {
    if (va === vb) return byLabel(depth === 0 ? -1 : 1);
    return va === null ? 1 : -1;
  }
  if (va !== vb) return dir * (va - vb);
  return byLabel(depth === 0 ? -1 : 1);
}

/** Copy of the tree with siblings sorted at every level. */
export function sortReportNodes(
  nodes: ReportTreeNodeV2[],
  sort: ViewSort | null,
  index: MetricIndex,
  depth = 0,
): ReportTreeNodeV2[] {
  return [...nodes]
    .sort((a, b) => compareNodes(a, b, depth, sort, index))
    .map((node) => ({
      ...node,
      children: sortReportNodes(node.children ?? [], sort, index, depth + 1),
    }));
}

/** Display label of a node: date buckets per granularity, others verbatim. */
export function nodeLabel(
  node: ReportTreeNodeV2,
  depth: number,
  granularity: ReportGranularity,
  range: BucketRange,
): string {
  return depth === 0
    ? formatBucketLabel(node.key, granularity, range)
    : node.label;
}

/** Stable id of a node across runs (keeps expand/collapse state). */
export const childNodeId = (parentId: string, node: ReportTreeNodeV2) =>
  `${parentId}/${node.dimension}:${encodeURIComponent(node.key)}`;

export interface TreeCellContext {
  columns: ReportMetricDef[];
  index: MetricIndex;
  granularity: ReportGranularity;
  range: BucketRange;
  /** How many group-bys the run has. Rows above the last level render as
   *  group rows even when empty (a day with no sales under it). */
  groupCount?: number;
}

/** Formatted cells for one value row (a node or the totals). */
export function formatCells(
  values: Array<number | null>,
  { columns, index }: Pick<TreeCellContext, "columns" | "index">,
): Record<string, string> {
  const cells: Record<string, string> = {};
  columns.forEach((m) => {
    cells[m.key] = formatMetricValue(valueOf(values, index, m.key), m.format);
  });
  return cells;
}

/** GroupedTreeTable nodes for an already-sorted report tree. */
export function toTreeTableNodes(
  nodes: ReportTreeNodeV2[],
  ctx: TreeCellContext,
  depth = 0,
  parentId = "",
): TreeTableNode[] {
  return nodes.map((node) => {
    const id = childNodeId(parentId, node);
    const label = nodeLabel(node, depth, ctx.granularity, ctx.range);
    return {
      id,
      label,
      title: label,
      cells: formatCells(node.values, ctx),
      children: toTreeTableNodes(node.children ?? [], ctx, depth + 1, id),
      group: depth < (ctx.groupCount ?? 0),
    };
  });
}

export interface FlatReportRow {
  node: ReportTreeNodeV2;
  depth: number;
  label: string;
}

/** Depth-first rows of a sorted tree (every node, regardless of collapse). */
export function flattenReportNodes(
  nodes: ReportTreeNodeV2[],
  granularity: ReportGranularity,
  range: BucketRange,
): FlatReportRow[] {
  const out: FlatReportRow[] = [];
  const walk = (list: ReportTreeNodeV2[], depth: number) => {
    list.forEach((node) => {
      out.push({
        node,
        depth,
        label: nodeLabel(node, depth, granularity, range),
      });
      walk(node.children ?? [], depth + 1);
    });
  };
  walk(nodes, 0);
  return out;
}

/** Grouping label per tree depth: the date level ("Date", or "Week" /
 *  "Month" for those granularities) then each group-by. */
export function levelLabelsOf(
  dateLabel: string,
  granularityLabel: string | undefined,
  granularity: ReportGranularity,
  groupLabels: string[],
): string[] {
  const head =
    granularity === "day" ? dateLabel : granularityLabel || dateLabel;
  return [head, ...groupLabels];
}
